import 'react-dates/initialize'
import 'react-dates/lib/css/_datepicker.css'

import { datadogRum } from '@datadog/browser-rum'
import { Processing } from '@paywithextend/components'
import { useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { initializeApp } from 'firebase/app'
import {
  fetchAndActivate,
  fetchConfig,
  getRemoteConfig,
} from 'firebase/remote-config'
import _, { debounce } from 'lodash'
import moment from 'moment-timezone'
import Pusher, { Authorizer } from 'pusher-js'
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useCookies } from 'react-cookie'
import { useDispatch, useSelector } from 'react-redux'
import { Route, Switch } from 'react-router'
import { IntercomProvider } from 'react-use-intercom'
import {
  AnalyticsPageName,
  AnalyticsUserTraits,
} from 'src/analytics/analytics.models'
import { analyticsPageNames } from 'src/analytics/AnalyticsContext'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  getAlertStatus,
  getAuthedAlertStatus,
} from 'src/app/AlertBar/redux/alertBar.actions'
import Authed from 'src/app/Authed'
import AuthenticationAgnostic from 'src/app/AuthenticationAgnostic'
import { invalidateCardFeedQueriesFromPusher } from 'src/app/cardFeedPusher'
import { createCreditCardStatisticsPusherSync } from 'src/app/creditCardStatisticsPusher'
import { getNotificationsCount } from 'src/app/Notifications/redux/notifications.actions'
import { selectFetchingInvites } from 'src/app/OrganizationInvites/redux/organizationInvites.selectors'
import { removeOrganizationInvite } from 'src/app/PeopleInvitedToOrg/redux/peopleInvitedToOrg.actions'
import { selectEmailSentFromState } from 'src/app/ResetPassword/redux/resetPassword.selectors'
import {
  makeResponseErrorHandler,
  type ResponseErrorData,
} from 'src/app/responseErrorHandler'
import {
  createSetIsToastOpen,
  createSetUnhideToast,
} from 'src/app/Toast/redux/toast.actions'
import { selectForceHideToast } from 'src/app/Toast/redux/toast.selectors'
import Toast, {
  CreateBulkReceiptAutoMatchFailureToastMessage,
  CreateBulkReceiptAutoMatchPartialToastMessage,
  CreateBulkReceiptAutoMatchSuccessToastMessage,
  CreateBulkVirtualCardAndInvitedUsersSuccessToastMessage,
  CreateBulkVirtualCardFailureToastMessage,
  CreateBulkVirtualCardSuccessToastMessage,
  CreateBulkVirtualCardToastMessage,
} from 'src/app/Toast/Toast'
import Unauthed from 'src/app/Unauthed'
import {
  createCompleteReceiptsAutoMatchBulkUpload,
  getAutoMatchReceiptsEnabled,
  getReceiptsAutoMatchBulkUploadPushStatus,
} from 'src/app/UploadReceiptsAutoMatchPopup/redux/uploadReceiptsAutoMatch.actions'
import { ReceiptAutoMatchPushTaskStatus } from 'src/app/UploadReceiptsAutoMatchPopup/redux/uploadReceiptsAutoMatch.reducers'
import {
  selectReceiptsAutoMatchIsCreatingExpensesFromState,
  selectReceiptsAutoMatchPushTaskListFromState,
} from 'src/app/UploadReceiptsAutoMatchPopup/redux/uploadReceiptsAutoMatch.selectors'
import DowngradedToStarterModal from 'src/component-modal/DowngradedToStarterModal/DowngradedToStarterModal'
import SaasActionRequiredModal, {
  SaasActionRequiredType,
} from 'src/component-modal/SaasActionRequiredModal/SaasActionRequiredModal'
import SessionExpiringPopup from 'src/components/SessionExpiringPopup/SessionExpiringPopup'
import SvgAlertCircledFilled2 from 'src/components/Svgs/SvgAlertCircledFilled2'
import { Environments } from 'src/configurations'
import { noRedirectURLs } from 'src/constants'
import AstradaContextProvider from 'src/context/AstradaContext/AstradaContext'
import useGlobalModal from 'src/context/GlobalModalContext/useGlobalModal'
import { useSessionStatus } from 'src/context/SessionStatusProvider/useSessionStatus'
import {
  CookieNames,
  CookieValuePaths,
  setPerSessionValuesCookie,
} from 'src/cookies'
import {
  AuthTypes,
  ExtendLocales,
  HidableToastNames,
  OrganizationInviteActions,
  PusherEvents,
  SupportedBrands,
} from 'src/enums'
import { AsyncJobPollers } from 'src/feature/AsyncJobPoller/AsyncJobPoller'
import { firebaseConfig, firebaseConfigProd } from 'src/firebaseConfigurations'
import { useChangedEffect, useCookieState } from 'src/hooks'
import { useAuthTokens } from 'src/hooks/useAuthTokens'
import { useCognitoSessionValid } from 'src/hooks/useCognitoSessionValid'
import useOrgUpdateAwaiter, {
  OrgUpdateAwaiter,
} from 'src/hooks/useOrgUpdateAwaiter'
import { useSyncTokenCookies } from 'src/hooks/useSyncTokenCookies'
import {
  applyInitialUnauthedLocale,
  resolveInitialUnauthedLocale,
} from 'src/localization/unauthenticatedLocale'
import {
  CreditCardListModel,
  PhysicalCardStatus,
} from 'src/models/creditCard.models'
import { RemoteConfigModel } from 'src/models/remoteConfig.models'
import {
  JobStatus,
  VirtualCardPushTaskStatus,
} from 'src/models/virtualCardPushTask.models'
import {
  AuthenticatedFlatArchitectureRoutes,
  authenticationAgnosticAppRoute,
  PublicFlatArchitectureRoutes,
} from 'src/navigation/appRoutes'
import { connectedPush } from 'src/navigation/Lnk'
import {
  cspStub,
  manageOrgInviteUrlStub,
  onboardingUrl,
  orgInviteDecisionUrl,
} from 'src/navigation/urls'
import { startAsyncJobPoll } from 'src/queries/asyncJobs/asyncJobPoll.store'
import { useQueryBrandRedirectSuggestions } from 'src/queries/auth.queries'
import { getOrgIntegrations } from 'src/queries/organizations/integrations/integrations.actions'
import { orgOutOfPocketExpensesQueries } from 'src/queries/organizations/outOfPocketExpenses/outOfPocketExpenses.queries'
import { orgReceiptManagementQueries } from 'src/queries/organizations/receiptManagement/receiptManagement.queries'
import { useQueryOrganizationSaasTier } from 'src/queries/organizations/saas.queries'
import { createPaymentConnectionPusherSync } from 'src/queries/payments/payments.queries'
import { handleEntityPusherEvent } from 'src/queries/pusherEntitySync'
import {
  authQueryKeys,
  creditCardsQueryKeys,
  defaultQueryCacheOptions,
  orgQueryKeys,
} from 'src/queries/queries.constants'
import { sessionConfigQueries } from 'src/queries/session/config/config.queries'
import { useQueryStatistics } from 'src/queries/statistics/statistics.queries'
import { StatisticsKeys } from 'src/queries/statistics/statistics.queries.constants'
import { queryTransactionDetail } from 'src/queries/transactions.queries'
import { useQueryOwnUser } from 'src/queries/users.queries'
import { createVirtualCardPusherSync } from 'src/queries/virtualCards.queries'
import {
  createResetAppState,
  createSetIsBrandRedirectPopupOpen,
  createSetIsSessionExpiringPopupOpen,
} from 'src/redux/actions/app.actions'
import {
  createResetMfaCodeStateAction,
  createResetPasswordResetStateAction,
  createSetUnauthedLocaleAction,
  createUpdateAcceptOrgInviteAction,
  createUpdateOrgInviteErrorAction,
  getCspMe,
  getHeartbeat,
  getMe,
  POST_SIGNIN_SUCCESS,
  postAuth0RenewAuth,
  postAuthType,
  postCognitoRenewAuth,
} from 'src/redux/actions/auth.actions'
import { createResetDataState } from 'src/redux/actions/data.actions'
import { getCreditCard } from 'src/redux/actions/data/creditCards.actions'
import { getCreditCards } from 'src/redux/actions/data/creditCards.actions'
import {
  getOrganizationInvite,
  getOrganizations,
  getUserOrganizationInvites,
} from 'src/redux/actions/data/organizations.actions'
import { getUser } from 'src/redux/actions/data/users.actions'
import { getBulkUploadPushStatus } from 'src/redux/actions/data/virtualCardPushTasks.actions'
import {
  createPostBulkVirtualCardCompletionAction,
  getBulkVirtualCardJobStatus,
  getBulkVirtualCardJobStatusByTaskId,
} from 'src/redux/actions/data/virtualCards.actions'
import { getFirebaseRemoteConfig } from 'src/redux/actions/session.actions'
import {
  selectIsAttachmentPopupOpenFromState,
  selectIsSessionExpiringPopupOpenFromState,
} from 'src/redux/selectors/app.selectors'
import {
  selectAcceptOrgInviteFromState,
  selectAuthTypeFromState,
  selectDecodedCspJwt,
  selectDecodedJwt,
  selectEmailFromState,
  selectIsAcceptOrgInvitePopupDismissed,
  selectIsCspAuthenticatedFromState,
  selectMfaSentFromState,
  selectNullSafeLocaleFromState,
  selectPerSessionValues,
  selectUserIdFromState,
  selectUserListModelFromState,
} from 'src/redux/selectors/auth.selectors'
import { selectAuthedJsonApiRequestConfig } from 'src/redux/selectors/axios.selectors'
import { selectCreditCardListModelsFromState } from 'src/redux/selectors/data/creditCards.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import {
  selectOrganizationInvitesModelsFromState,
  selectOrganizationsListModelsFromState,
  selectUserExplicitOrganizationListModelFromState,
} from 'src/redux/selectors/data/organizations.selectors'
import { selectOrgIntegrationsModelsFromState } from 'src/redux/selectors/data/orgIntegrations.selectors'
import { selectVirtualCardPushTaskListModelsFromState } from 'src/redux/selectors/data/virtualCardPushTasks.selectors'
import { selectVirtualCardBulkJobModels } from 'src/redux/selectors/data/virtualCards.selectors'
import {
  selectCalculatedBackgroundLocation,
  selectIsLeftFlyoutOpen,
  selectIsPopupOpen,
  selectIsRightFlyoutOpen,
  selectLocationFromState,
  selectQueryParams,
} from 'src/redux/selectors/router.selectors'
import {
  selectBrandName,
  selectEnvConfigFromState,
  selectEnvironmentFromState,
  selectLocalAuthBypassFromState,
  selectLocalUserIdFromState,
  selectPusherChannelPrefix,
} from 'src/redux/selectors/session.selectors'
import { getFeatureFlagsFromRemoteConfigAction } from 'src/utils/remoteConfig.utils'
import { css } from 'styled-components'

const HEART_BEAT_INTERVAL_MILLISECONDS = 30000
const REFRESH_TOKEN_EXPIRATION_MILLISECONDS = 300000
// Edit SESSION_EXPIRING_MAX_DURATION_MILLISECONDS to test session expiration pop up sooner. 840000 will pop the modal in 1 minute
const SESSION_EXPIRING_MAX_DURATION_MILLISECONDS = 60000
const RENEWAUTH_DEBOUNCE_MILLISECONDS = 10000

// Structurally valid JWT for local auth bypass. alg:none, sub:local-dev, exp:year 2100.
// exp is set to year 2100 so SessionExpiringPopup never triggers in local dev.
const LOCAL_DEV_JWT =
  'eyJhbGciOiJub25lIiwidHlwIjoiSldUIn0.eyJzdWIiOiJsb2NhbC1kZXYiLCJleHAiOjQxMDI0NDQ4MDAsImlhdCI6MTcwMDAwMDAwMH0.'

export const PusherContext = createContext<Pusher | undefined>(undefined)

// Centralized at App level (not per-consumer) so a consumer's effect can't
// race the App-level Pusher subscription on initial mount.
export const OrgUpdateAwaiterContext = createContext<
  OrgUpdateAwaiter['awaitOrgUpdateOrTimeout']
>(() => {
  throw new Error('OrgUpdateAwaiterContext used outside of its provider')
})

const PARTNER_BANKS_LIST = [
  //Current Enum values not accurate. Using this list for checks
  'AMEX',
  'AMEXCEL',
  'BMO-MCIC',
  'BMO_BPC',
  'BOTW',
  'BOTW-MCIC',
  'BOTW_BPC',
  'CNB',
  'COMDATA',
  'COMDATA-GC',
  'HSBC',
  'HSBC-MCIC',
  'HSBC_BPC',
  'JPM',
  'PACWEST',
  'PACWEST_BPC',
  'PNC_VISA_TSYS_6940',
  'PNC_VISA_TSYS_8975',
  'PWB-MCIC',
  'REGIONS',
  'SVB',
  'TCB',
  'TCB_TSYS',
]

const App: React.FC = (): React.ReactElement => {
  const creditCards = useSelector(selectCreditCardListModelsFromState)
  const authedJsonApiRequestConfig = useSelector(
    selectAuthedJsonApiRequestConfig,
  )
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const envConfig = useSelector(selectEnvConfigFromState)
  const isSessionExpiringPopupOpen = useSelector(
    selectIsSessionExpiringPopupOpenFromState,
  )
  const { isAuthenticated } = useSessionStatus()
  const { jwt, refreshToken, cspJwt, cspRefreshToken } = useAuthTokens()
  const decodedJwt = useSelector(selectDecodedJwt)
  const isCspAuthenticated = useSelector(selectIsCspAuthenticatedFromState)
  const decodedCspJwt = useSelector(selectDecodedCspJwt)
  const locale = useSelector(selectNullSafeLocaleFromState)
  const location = useSelector(selectLocationFromState)
  const pusherChannelPrefix = useSelector(selectPusherChannelPrefix)
  const user = useSelector(selectUserListModelFromState)
  const userId = useSelector(selectUserIdFromState)
  const virtualCardPushTasks = useSelector(
    selectVirtualCardPushTaskListModelsFromState,
  )
  const newBulkUploadJobs = useSelector(selectVirtualCardBulkJobModels)
  const isPopupOpen = useSelector(selectIsPopupOpen)
  const isLeftFlyoutOpen = useSelector(selectIsLeftFlyoutOpen)
  const isRightFlyoutOpen = useSelector(selectIsRightFlyoutOpen)
  const isAttachmentPopupOpen = useSelector(
    selectIsAttachmentPopupOpenFromState,
  )
  const organizations = useSelector(selectOrganizationsListModelsFromState)
  const currentOrganization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const currentOrgIntegrations = useSelector(
    selectOrgIntegrationsModelsFromState,
  )
  const authEmail = useSelector(selectEmailFromState)
  const env = useSelector(selectEnvironmentFromState)
  const authType = useSelector(selectAuthTypeFromState)
  const localAuthBypass = useSelector(selectLocalAuthBypassFromState)
  const localUserId = useSelector(selectLocalUserIdFromState)
  const resetPasswordEmailSent = useSelector(selectEmailSentFromState)
  const mfaCodeEmailSent = useSelector(selectMfaSentFromState)
  const forceHideToast = useSelector(selectForceHideToast)
  const orgInvites = useSelector(selectOrganizationInvitesModelsFromState)
  const isAcceptOrgInvitePopupDismissed = useSelector(
    selectIsAcceptOrgInvitePopupDismissed,
  )
  const perSessionValues = useSelector(selectPerSessionValues)
  const fetchingInvites = useSelector(selectFetchingInvites)
  // User-traits statistics (analytics identify): the same six counts the
  // legacy auth-time dispatch effect fed via Redux, now fetched directly.
  const { results: traitsStatistics } = useQueryStatistics({
    statisticsKeys: [
      StatisticsKeys.VirtualCardsRecipientMeCount,
      StatisticsKeys.CreditCardsSourceCount,
      StatisticsKeys.CreditCardsActiveSourceCount,
      StatisticsKeys.CreditCardsDelegateOwnerMeCount,
      StatisticsKeys.CreditCardsDelegateUserMeCount,
      StatisticsKeys.VirtualCardsCardholderOrViewerMeActiveAndInactiveCount,
    ] as const,
    enabled: isAuthenticated && userId !== undefined,
  })
  const receiptsAutoMatchPushTaskList = useSelector(
    selectReceiptsAutoMatchPushTaskListFromState,
  )
  const acceptOrgInvite = useSelector(selectAcceptOrgInviteFromState)
  const brandName = useSelector(selectBrandName)
  const queryParams = useSelector(selectQueryParams)
  const { analytics } = useAnalyticsContext()
  const { show_brand_redirect: showBrandRedirect } = useSelector(
    selectFeatureFlaggerFromState,
  )
  const isCreatingAutomatchExpenses = useSelector(
    selectReceiptsAutoMatchIsCreatingExpensesFromState,
  )
  const { data: orgSaasTier } = useQueryOrganizationSaasTier({
    orgId: currentOrganization?.id,
    enabled: isAuthenticated && currentOrganization?.id !== undefined,
    queryCacheOptions: defaultQueryCacheOptions,
  })

  const globalModal = useGlobalModal()
  const queryClient = useQueryClient()

  const completedReceiptsAutoMatchTasks = receiptsAutoMatchPushTaskList.filter(
    (task) => task.status !== ReceiptAutoMatchPushTaskStatus.PENDING,
  )

  const userHasPendingOrgInvite =
    Object.values(orgInvites)?.length > 0 &&
    !user?.isInExplicitOrg &&
    !isAcceptOrgInvitePopupDismissed

  const bankProperties = useMemo(() => {
    const userCards = Object.values(creditCards) as CreditCardListModel[]

    if (userCards.length === 0) {
      return {
        hasActiveAstradaCard: false,
        partnerBanks: undefined,
        allIssuerBanks: undefined,
      }
    }

    const partnerBanksSet = new Set<string>()
    const allBanksSet = new Set<string>()
    let hasActiveAstradaCard = false

    for (const card of userCards) {
      const bankName = card.issuer
      const isActivePhysicalCard =
        card.physicalCard.status === PhysicalCardStatus.Active

      if (isActivePhysicalCard) {
        hasActiveAstradaCard = true
      }

      if (bankName) {
        allBanksSet.add(bankName)

        if (!hasActiveAstradaCard && PARTNER_BANKS_LIST.includes(bankName)) {
          partnerBanksSet.add(bankName)
        }
      }
    }

    return {
      hasActiveAstradaCard,
      partnerBanks:
        !hasActiveAstradaCard && partnerBanksSet.size > 0
          ? Array.from(partnerBanksSet).join(', ')
          : undefined,
      allIssuerBanks:
        hasActiveAstradaCard && allBanksSet.size > 0
          ? Array.from(allBanksSet).join(', ')
          : undefined,
    }
  }, [creditCards])

  const [cookies] = useCookies([CookieNames.FeatureFlagger])

  useEffect((): void => {
    // keeping the firebase config code here because we can't call it from the server side
    // having firebase config in App.tsx allows it to trigger as soon as a user opens the app rather than waiting for signin
    const app = initializeApp(
      env === Environments.Prod ? firebaseConfigProd : firebaseConfig,
    )
    const remoteConfig = getRemoteConfig(app)
    remoteConfig.settings.minimumFetchIntervalMillis =
      env === Environments.Prod ? 900000 : 30

    fetchAndActivate(remoteConfig).then(
      (response: unknown) => {
        if (response) {
          dispatch(
            getFeatureFlagsFromRemoteConfigAction(
              remoteConfig,
              cookies.feature_flagger,
              env,
            ),
          )
        } else {
          fetchConfig(remoteConfig).then(
            () => {
              dispatch(
                getFeatureFlagsFromRemoteConfigAction(
                  remoteConfig,
                  cookies.feature_flagger,
                  env,
                ),
              )
            },
            (err: unknown) => {
              dispatch(
                getFirebaseRemoteConfig(
                  RemoteConfigModel.defaultConfig(),
                  cookies.feature_flagger,
                ),
              )
              /* eslint-disable-next-line no-console */
              console.log(err)
              return Promise.reject(err)
            },
          )
        }
      },
      (err: unknown) => {
        dispatch(
          getFirebaseRemoteConfig(
            RemoteConfigModel.defaultConfig(),
            cookies.feature_flagger,
          ),
        )
        /* eslint-disable-next-line no-console */
        console.log(err)
        return Promise.reject(err)
      },
    )
  }, [userId])

  const email = user?.email || authEmail

  const dispatch = useDispatch()

  // One rate limiter + circuit breaker per session (not per event / re-subscribe):
  // memoized on the stable queryClient/dispatch so the pusher fetch ceiling
  // survives token-refresh reconnects instead of resetting.
  const virtualCardPusherSync = useMemo(
    () => createVirtualCardPusherSync({ queryClient, dispatch }),
    [queryClient, dispatch],
  )

  const paymentConnectionPusherSync = useMemo(
    () =>
      createPaymentConnectionPusherSync({
        queryClient,
        organizationId: user?.organizationId,
      }),
    [queryClient, user?.organizationId],
  )

  const creditCardStatisticsPusherSync = useMemo(
    () => createCreditCardStatisticsPusherSync({ queryClient }),
    [queryClient],
  )

  // Local auth bypass: skip Amplify, inject user directly.
  useEffect((): void => {
    if (localAuthBypass && !isAuthenticated) {
      // localStorage override from FeatureFlagger user switcher takes priority
      let localOverride: string | null = null
      try {
        localOverride = localStorage.getItem('LOCAL_USER_ID_OVERRIDE')
      } catch {
        // localStorage may be inaccessible during page transitions
      }
      const userId = localOverride || localUserId
      dispatch({
        type: POST_SIGNIN_SUCCESS,
        payload: {
          token: LOCAL_DEV_JWT,
          refreshToken: 'local-dev-refresh',
          user: userId ? { id: userId } : undefined,
        },
      })
      // Fetch real user data from the API after injecting auth state
      dispatch(getMe())
    }
  }, [localAuthBypass, isAuthenticated, localUserId, dispatch])

  useEffect((): void => {
    if (isAuthenticated && currentOrganization !== undefined) {
      dispatch(getCreditCards({}))
    }
  }, [isAuthenticated, currentOrganization?.id])

  // scroll
  useEffect((): void => {
    window.scrollTo(0, 0)
  }, [calculatedBackgroundLocation])

  // modal open scroll prevention
  useEffect(() => {
    const isModalOpen =
      isPopupOpen ||
      isLeftFlyoutOpen ||
      isRightFlyoutOpen ||
      isAttachmentPopupOpen
    if (isModalOpen) {
      document.body.classList.add('modal-open')
    } else {
      document.body.classList.remove('modal-open')
    }
  }, [
    isPopupOpen,
    isLeftFlyoutOpen,
    isRightFlyoutOpen,
    isAttachmentPopupOpen,
    isSessionExpiringPopupOpen,
  ])

  // pusher
  const [pusher, setPusher] = useState<Pusher | undefined>()

  const { awaitOrgUpdateOrTimeout, dispatchOrgUpdate } = useOrgUpdateAwaiter()
  useEffect((): void => {
    if (pusher !== undefined) {
      pusher.disconnect()
    }

    const newPusher =
      envConfig.PUSHER_KEY !== undefined &&
      envConfig.PUSHER_CLUSTER !== undefined &&
      userId !== undefined
        ? new Pusher(envConfig.PUSHER_KEY, {
            authorizer: (): Authorizer => ({
              authorize: (socketId, callback): void => {
                axios
                  .post(
                    '/pusherauth',
                    {
                      socket_id: socketId,
                      channel_name: `${pusherChannelPrefix}${userId}`,
                    },
                    authedJsonApiRequestConfig,
                  )
                  .then((response) => callback(null, response.data))
                  .catch((error) =>
                    callback(
                      {
                        name: 'Error',
                        message: error.response,
                      },
                      error.response,
                    ),
                  )
              },
            }),
            cluster: envConfig.PUSHER_CLUSTER,
            // Local dev only: when PUSHER_WS_HOST is set (seeded via SSM for the
            // local stack) repoint the websocket at a self-hosted Soketi server.
            // Absent in cloud, so this spread is empty and behavior is unchanged.
            // Loader hands these back as strings, so coerce to number/boolean.
            ...(envConfig.PUSHER_WS_HOST !== undefined
              ? {
                  wsHost: envConfig.PUSHER_WS_HOST,
                  wsPort: Number(envConfig.PUSHER_WS_PORT),
                  wssPort: Number(envConfig.PUSHER_WS_PORT),
                  forceTLS: envConfig.PUSHER_FORCE_TLS === 'true',
                  enabledTransports: ['ws', 'wss'] as ('ws' | 'wss')[],
                  disableStats: true,
                }
              : {}),
          })
        : undefined

    if (newPusher !== undefined) {
      const channel = newPusher.subscribe(`${pusherChannelPrefix}${userId}`)

      channel.bind(
        PusherEvents.BulkReceiptCapture,
        (data: { id: string; action: string }) => {
          dispatch(getReceiptsAutoMatchBulkUploadPushStatus(data.id))
        },
      )

      channel.bind(
        PusherEvents.BulkUpload,
        (data: { id: string; action: string }) => {
          dispatch(getBulkUploadPushStatus(data.id))
        },
      )

      channel.bind(
        PusherEvents.CreditCard,
        (data: { id: string; action: string }) => {
          dispatch(getCreditCard(data.id))
          // Rate-limited per session; over budget it marks stale instead of
          // refetching. Only mounted consumers refetch.
          creditCardStatisticsPusherSync()
          // Reactive burst path: coalesce onto the in-flight /features fetch
          // instead of aborting + relaunching one per pusher event.
          queryClient.invalidateQueries(
            {
              // Features are identity-scoped now, so target the whole features
              // family (not a bare feature-key that no longer matches the
              // identity-scoped cache entry). Only the mounted current-identity
              // features query refetches.
              queryKey: authQueryKeys.all(),
            },
            { cancelRefetch: false },
          )
          // Legacy thunk above only refreshes Redux; mirror into RQ so
          // useQueryCreditCard consumers (AccountSettings, etc.) refetch.
          // `exact: true` so permissions(id) — same prefix — isn't dragged along.
          queryClient.invalidateQueries({
            queryKey: creditCardsQueryKeys.detail(data.id),
            exact: true,
          })
        },
      )

      channel.bind(
        PusherEvents.Transaction,
        async (data: { id: string; action: string }) => {
          queryTransactionDetail(
            queryClient,
            authedJsonApiRequestConfig,
            data.id,
          )
        },
      )

      channel.bind(
        PusherEvents.AsyncJob,
        (data: { id: string; action: string }) => {
          // Start (or join) the React Query status poll for this bulk job; the
          // <AsyncJobPollers> host drives the toasts + invalidation (was the
          // getAsyncTransactionUpdateJobStatus Redux thunk).
          startAsyncJobPoll(data.id)
        },
      )

      channel.bind(
        PusherEvents.CardFeed,
        (data: { id: string; action: string }) => {
          invalidateCardFeedQueriesFromPusher(
            queryClient,
            user?.organizationId,
            data,
          )
        },
      )

      channel.bind(
        PusherEvents.PaymentConnection,
        (data: { id: string; action: string }) => {
          // Rate-limited fetch-and-patch. Overflow and breaker-open paths mark
          // the connection family stale without launching another request.
          void handleEntityPusherEvent(data, paymentConnectionPusherSync)
        },
      )

      channel.bind(
        PusherEvents.User,
        (data: { id: string; action: string }) => {
          dispatch(getUser(data.id))
        },
      )

      channel.bind(
        PusherEvents.VirtualCard,
        (data: { id: string; action: string }) => {
          // Rate-limited fetch-and-patch — see createVirtualCardPusherSync.
          void handleEntityPusherEvent(data, virtualCardPusherSync)
        },
      )

      channel.bind(
        PusherEvents.OrganizationInvite,
        (data: { id: string; action: string }) => {
          switch (data.action) {
            case OrganizationInviteActions.CREATED: {
              dispatch(getOrganizationInvite(data.id))
              break
            }
            case OrganizationInviteActions.UPDATED: {
              dispatch(removeOrganizationInvite(data.id))
              break
            }
          }
        },
      )

      channel.bind(
        PusherEvents.BulkVirtualCardAction,
        (data: { id: string; action: string }) => {
          dispatch(getBulkVirtualCardJobStatus(data.id))
        },
      )

      channel.bind(
        PusherEvents.BulkVirtualCardTask,
        (data: { id: string; action: string }) => {
          dispatch(getBulkVirtualCardJobStatusByTaskId(data.id))
        },
      )

      channel.bind(
        PusherEvents.SaasOrganization,
        async (data: { id: string; action: string }) => {
          // Run ahead of the await so consumers waiting on the event aren't
          // gated on the cache invalidations below.
          dispatchOrgUpdate(data.id)

          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: orgQueryKeys.saasTier(data.id),
            }),
            queryClient.invalidateQueries({
              queryKey: orgQueryKeys.organizationSubscription(data.id),
            }),
            queryClient.invalidateQueries({
              queryKey: orgQueryKeys.organizationUsages(data.id),
            }),
          ])
        },
      )
    }

    setPusher(newPusher)
  }, [
    envConfig,
    userId,
    user?.organizationId,
    authedJsonApiRequestConfig,
    dispatchOrgUpdate,
    paymentConnectionPusherSync,
    queryClient,
  ])

  // jwt and refresh token cookies
  useSyncTokenCookies(envConfig)

  // update per-session cookie when per-session values change
  useEffect((): void => {
    if (perSessionValues !== undefined)
      setPerSessionValuesCookie(perSessionValues)
  }, [perSessionValues])

  // receipts auto-match push tasks toasts
  useEffect(() => {
    if (
      completedReceiptsAutoMatchTasks.length ===
        receiptsAutoMatchPushTaskList.length &&
      completedReceiptsAutoMatchTasks.length > 0
    ) {
      dispatch(createSetIsToastOpen(false))
      dispatch(getNotificationsCount())
      const successTasks = receiptsAutoMatchPushTaskList.filter(
        (task) => task.status === ReceiptAutoMatchPushTaskStatus.SUCCESS,
      )
      if (successTasks.length === receiptsAutoMatchPushTaskList.length) {
        dispatch(
          createSetIsToastOpen(true, {
            toastMessage: (
              <CreateBulkReceiptAutoMatchSuccessToastMessage
                isCreatingExpenses={isCreatingAutomatchExpenses}
              />
            ),
          }),
        )
      } else if (successTasks.length > 0) {
        dispatch(
          createSetIsToastOpen(true, {
            toastMessage: (
              <CreateBulkReceiptAutoMatchPartialToastMessage
                successful={successTasks.length}
                failed={
                  receiptsAutoMatchPushTaskList.length - successTasks.length
                }
                isCreatingExpenses={isCreatingAutomatchExpenses}
              />
            ),
            image: (
              <SvgAlertCircledFilled2
                css={css`
                  transform: scale(0.8);
                  margin-left: -10px;
                `}
              />
            ),
            partialSuccessColorScheme: true,
          }),
        )
      } else if (successTasks.length === 0) {
        dispatch(
          createSetIsToastOpen(true, {
            toastMessage: (
              <CreateBulkReceiptAutoMatchFailureToastMessage
                isCreatingExpenses={isCreatingAutomatchExpenses}
              />
            ),
            hideImage: true,
            failureColorScheme: true,
          }),
        )
      }

      dispatch(createCompleteReceiptsAutoMatchBulkUpload())
    }
  }, [completedReceiptsAutoMatchTasks.length])

  // virtual card push tasks toast
  useEffect((): void => {
    const bulkUploadJobsArray = Object.values(newBulkUploadJobs).filter(
      (job) => job.status === JobStatus.Running,
    )

    bulkUploadJobsArray.map((job) => {
      const pushTasksArray = Object.values(virtualCardPushTasks).filter(
        (task) => task.uploadId === job.id,
      )
      const failedTasks = pushTasksArray.filter(
        (task) => task.status === VirtualCardPushTaskStatus.Failed,
      )
      const completedTasks = pushTasksArray.filter(
        (task) => task.status === VirtualCardPushTaskStatus.Completed,
      )

      if (pushTasksArray.length > 0) {
        if (!forceHideToast) {
          dispatch(
            createSetIsToastOpen(true, {
              toastMessage: (
                <CreateBulkVirtualCardToastMessage
                  displayName={job?.displayName || ''}
                  virtualCardPushTasks={pushTasksArray}
                />
              ),
              hidableToastName: HidableToastNames.BulkVirtualCardAction,
              inProgressColorScheme: true,
              image: (
                <Processing
                  css={css`
                    transform: scale(1.2);
                  `}
                />
              ),
              bulkToast: true,
            }),
          )
        }
        if (completedTasks.length === pushTasksArray.length) {
          dispatch(
            createSetUnhideToast(HidableToastNames.BulkVirtualCardAction),
          )
          dispatch(createPostBulkVirtualCardCompletionAction(job.id))
          if (job.invitingUsers) {
            dispatch(
              createSetIsToastOpen(true, {
                toastMessage: (
                  <CreateBulkVirtualCardAndInvitedUsersSuccessToastMessage
                    displayName={job?.displayName || ''}
                    virtualCardPushTasks={pushTasksArray}
                  />
                ),
              }),
            )
          } else {
            dispatch(
              createSetIsToastOpen(true, {
                toastMessage: (
                  <CreateBulkVirtualCardSuccessToastMessage
                    displayName={job?.displayName || ''}
                    virtualCardPushTasks={pushTasksArray}
                  />
                ),
              }),
            )
          }
        }
        if (failedTasks.length > 0) {
          dispatch(
            createSetUnhideToast(HidableToastNames.BulkVirtualCardAction),
          )
          dispatch(
            createSetIsToastOpen(true, {
              toastMessage: (
                <CreateBulkVirtualCardFailureToastMessage
                  displayName={job?.displayName || ''}
                />
              ),
              failureColorScheme: true,
              hideImage: true,
            }),
          )
        }
      }
    })
  }, [virtualCardPushTasks, newBulkUploadJobs, forceHideToast])

  // triggers for becoming authenticated/unauthenticated
  useChangedEffect((): void => {
    try {
      window.localStorage.setItem('authChangedTrigger', 'true')
    } catch {
      return
    }
  }, [jwt, refreshToken, cspJwt, cspRefreshToken])
  const handleLocalStorage = (): void => {
    try {
      const authChangedTrigger =
        window.localStorage.getItem('authChangedTrigger')
      window.localStorage.removeItem('authChangedTrigger')
      if (authChangedTrigger && !document.hasFocus()) {
        window.location.reload()
      }
    } catch {
      return
    }
  }
  useEffect(() => {
    window.addEventListener('storage', handleLocalStorage) // triggers only on NON active tabs
    return (): void => {
      window.removeEventListener('storage', handleLocalStorage)
    }
  }, [])

  // locale
  useEffect(() => {
    if (locale !== undefined) {
      moment.locale(locale)
      document.documentElement.setAttribute('lang', locale.toLowerCase())
    }
  }, [locale])

  // axios interceptor
  const responseInterceptor = useRef<number | undefined>()
  useEffect((): (() => void) => {
    responseInterceptor.current = axios.interceptors.response.use(
      (response) => response,
      makeResponseErrorHandler({
        authType,
        isAuthenticated,
        isCspAuthenticated,
        onAuthenticatedAuth0Unauthorized: () => dispatch(getHeartbeat()),
        onCspAuthenticatedAuth0Unauthorized: () => dispatch(getHeartbeat(true)),
        cognitoSessionValid: () => cognitoSessionValidRef.current(),
        downgradeActionRequiredType:
          SaasActionRequiredType.DOWNGRADE_ACTION_REQUIRED,
        userHasOrgAdminPermissions: user?.hasOrgAdminPermissions ?? false,
        showDowngradedToStarterModal: (data: ResponseErrorData) =>
          globalModal.showGlobalModal(
            <DowngradedToStarterModal
              isOpen
              onClose={() => globalModal.closeGlobalModal()}
              {...data}
            />,
          ),
        showSaasActionRequiredModal: (data: ResponseErrorData) =>
          globalModal.showGlobalModal(
            <SaasActionRequiredModal
              isOpen
              onClose={() => globalModal.closeGlobalModal()}
              {...data}
            />,
          ),
      }),
    )
    return (): void => {
      if (responseInterceptor.current !== undefined) {
        axios.interceptors.response.eject(responseInterceptor.current)
        responseInterceptor.current = undefined
      }
    }
  }, [isAuthenticated, isCspAuthenticated, user])

  // determine if cognito session is valid (only applies to Cognito auth)
  const cognitoSessionValid = useCognitoSessionValid({
    localAuthBypass,
    authType,
    isCspAuthenticated,
    decodedJwt,
    decodedCspJwt,
    analytics,
    dispatch,
  })

  // Keep a ref to the latest cognitoSessionValid so the 401 interceptor
  // (whose useEffect deps don't include jwt) always calls the current version.
  const cognitoSessionValidRef = useRef(cognitoSessionValid)
  cognitoSessionValidRef.current = cognitoSessionValid

  useEffect(() => {
    if (isAuthenticated || isCspAuthenticated) {
      cognitoSessionValid()
    }
  }, [cognitoSessionValid, isAuthenticated, isCspAuthenticated, authType])

  // heartbeat
  const heartbeatInterval = useRef<NodeJS.Timeout | undefined>()
  useEffect(() => {
    if (isCspAuthenticated && decodedCspJwt !== undefined) {
      heartbeatInterval.current = setInterval((): void => {
        const msUntilCspJwtExpiration = moment(
          new Date(decodedCspJwt.exp * 1000),
        ).diff(moment())
        if (
          msUntilCspJwtExpiration < SESSION_EXPIRING_MAX_DURATION_MILLISECONDS
        ) {
          dispatch(createSetIsSessionExpiringPopupOpen(true))
        }
      }, HEART_BEAT_INTERVAL_MILLISECONDS)
    } else if (isAuthenticated && decodedJwt !== undefined) {
      heartbeatInterval.current = setInterval((): void => {
        const msUntilJwtExpiration = moment(
          new Date(decodedJwt.exp * 1000),
        ).diff(moment())

        if (msUntilJwtExpiration < SESSION_EXPIRING_MAX_DURATION_MILLISECONDS) {
          dispatch(createSetIsSessionExpiringPopupOpen(true))
        }
      }, HEART_BEAT_INTERVAL_MILLISECONDS)
    }
    return (): void => {
      if (heartbeatInterval.current !== undefined) {
        clearInterval(heartbeatInterval.current)
      }
    }
  }, [isCspAuthenticated, isAuthenticated, decodedJwt, decodedCspJwt])

  // renewauth
  const handleAuthenticatedActions = useCallback(
    debounce((): void => {
      if (
        decodedCspJwt !== undefined &&
        cspRefreshToken !== undefined &&
        !isSessionExpiringPopupOpen
      ) {
        const msUntilCspJwtExpiration = moment(
          new Date(decodedCspJwt.exp * 1000),
        ).diff(moment())
        if (msUntilCspJwtExpiration < REFRESH_TOKEN_EXPIRATION_MILLISECONDS) {
          if (authType === AuthTypes.cognito) {
            dispatch(postCognitoRenewAuth(false, true))
          } else {
            // TODO: investigate this further. Was getting 403s when working on this pr (https://github.com/paywithextend/extend-app/pull/1884). Wrong refresh token being used?
            dispatch(postAuth0RenewAuth(email, true))
          }
        }
      } else if (
        decodedJwt !== undefined &&
        refreshToken !== undefined &&
        !isSessionExpiringPopupOpen
      ) {
        const msUntilJwtExpiration = moment(
          new Date(decodedJwt.exp * 1000),
        ).diff(moment())
        if (msUntilJwtExpiration < REFRESH_TOKEN_EXPIRATION_MILLISECONDS) {
          if (authType === AuthTypes.cognito) {
            dispatch(postCognitoRenewAuth())
          } else {
            dispatch(postAuth0RenewAuth(email))
          }
        }
      }
    }, RENEWAUTH_DEBOUNCE_MILLISECONDS),
    [
      decodedJwt,
      refreshToken,
      decodedCspJwt,
      cspRefreshToken,
      isSessionExpiringPopupOpen,
      authType,
    ],
  )

  useEffect(() => {
    window.addEventListener('mousedown', handleAuthenticatedActions)
    window.addEventListener('keydown', handleAuthenticatedActions)
    return (): void => {
      window.removeEventListener('mousedown', handleAuthenticatedActions)
      window.removeEventListener('keydown', handleAuthenticatedActions)
    }
  }, [handleAuthenticatedActions])

  // first login
  const {
    // cookieValue: firstLogin,
    updateCookieValue: setFirstLogin,
  } = useCookieState(CookieNames.Authed, CookieValuePaths.firstLogin, true)

  // first login
  const {
    cookieValue: unauthedLocaleCookie,
    updateCookieValue: setUnauthedLocaleCookie,
  } = useCookieState<ExtendLocales | undefined>(
    CookieNames.Unauthed,
    CookieValuePaths.unauthedLocale,
    undefined,
  )

  // set unauthed locale on app load
  useEffect((): void => {
    const initialLocale = resolveInitialUnauthedLocale(
      queryParams.locale,
      unauthedLocaleCookie,
    )
    applyInitialUnauthedLocale(
      initialLocale,
      (locale) => dispatch(createSetUnauthedLocaleAction(locale)),
      setUnauthedLocaleCookie,
    )
  }, [])

  // set unauthed locale user's locale to persist user locale into unauthed view (easier to do this here rather than every log out logic point)
  useEffect((): void => {
    if (isAuthenticated && user && queryParams.locale === undefined) {
      dispatch(createSetUnauthedLocaleAction(locale))
      setUnauthedLocaleCookie(locale) // set cookie too
    }
  }, [isAuthenticated, user])

  // check for brand suggestions in case  we want to redirect the user to log into a different brand
  const { data: brandRedirectSuggestions } = useQueryBrandRedirectSuggestions({
    enabled:
      showBrandRedirect && !perSessionValues?.isBrandSuggestionPopupDismissed,
  })

  // potentially open brand redirect popup
  useEffect(() => {
    if (brandRedirectSuggestions) {
      dispatch(
        createSetIsBrandRedirectPopupOpen(brandRedirectSuggestions.length > 0),
      )
    }
  }, [brandRedirectSuggestions])

  useEffect((): void => {
    if (isAuthenticated) {
      setFirstLogin(false)
      dispatch(postAuthType({ data: email }))
    }
  }, [isAuthenticated])

  useEffect((): void => {
    if (isAuthenticated && currentOrganization !== undefined) {
      void queryClient.prefetchQuery(
        orgReceiptManagementQueries.enabled(currentOrganization.id),
      )
      void queryClient.prefetchQuery(
        orgOutOfPocketExpensesQueries.enabled(currentOrganization.id),
      )
      dispatch(getAutoMatchReceiptsEnabled(currentOrganization.id))
    }
  }, [isAuthenticated, currentOrganization?.id])

  useEffect((): void => {
    if (isAuthenticated && userId) {
      dispatch(getNotificationsCount())
    }
  }, [isAuthenticated, userId])

  // config — pre-warm the session /config reference data at boot so option
  // lists are cached before consumers mount (staleTime Infinity dedupes the
  // consumers' useQuery calls to this one fetch per locale)
  useEffect((): void => {
    void queryClient.prefetchQuery(sessionConfigQueries.byLocale(locale))
  }, [locale])

  // reset data and app state when not authed
  useChangedEffect((): void => {
    if (!isAuthenticated && !isCspAuthenticated) {
      dispatch(createResetDataState())
      dispatch(createResetAppState())
    }
  }, [isAuthenticated, isCspAuthenticated])

  useEffect(() => {
    isAuthenticated
      ? dispatch(getAuthedAlertStatus())
      : dispatch(getAlertStatus())
  }, [isAuthenticated, isCspAuthenticated])

  // organizations
  useEffect((): void => {
    if (isAuthenticated && Object.keys(organizations).length === 0) {
      dispatch(getOrganizations())
    }
  }, [isAuthenticated, organizations])

  // organization integrations
  useEffect((): void => {
    if (
      isAuthenticated &&
      currentOrganization !== undefined &&
      !currentOrgIntegrations
    ) {
      dispatch(getOrgIntegrations(currentOrganization.id))
    }
  }, [isAuthenticated, currentOrganization, user])

  // get users organization invites
  useEffect((): void => {
    if (isAuthenticated) {
      dispatch(
        getUserOrganizationInvites({
          sortDirection: 'ASC',
          sortField: 'createdAt',
        }),
      )
    }
  }, [isAuthenticated])

  // route to onboarding name flow if user is logged in, the user object is in the redux store, and they are not part of an org
  useEffect((): void => {
    if (
      isAuthenticated &&
      user &&
      !user?.isInExplicitOrg &&
      !location.pathname.includes(manageOrgInviteUrlStub()) &&
      !location.pathname.includes(cspStub()) &&
      !noRedirectURLs?.includes(location.pathname) &&
      calculatedBackgroundLocation.pathname !== orgInviteDecisionUrl() &&
      !fetchingInvites
    ) {
      if (userHasPendingOrgInvite) {
        if (
          location.pathname !== orgInviteDecisionUrl() &&
          calculatedBackgroundLocation.pathname !== orgInviteDecisionUrl() &&
          !acceptOrgInvite
        ) {
          dispatch(connectedPush(orgInviteDecisionUrl()))
        }
      } else if (location.pathname !== onboardingUrl()) {
        dispatch(connectedPush(onboardingUrl()))
      }
    }
  }, [
    isAuthenticated,
    user,
    location,
    userHasPendingOrgInvite,
    orgInvites,
    fetchingInvites,
  ])

  useQueryOwnUser({ enabled: isAuthenticated && !isCspAuthenticated })

  useEffect(() => {
    if (isAuthenticated && Object.values(orgInvites)?.length === 0) {
      if (acceptOrgInvite === undefined) {
        if (isCspAuthenticated) {
          dispatch(getCspMe())
        }

        dispatch(createUpdateAcceptOrgInviteAction(false))
      } else if (acceptOrgInvite) {
        dispatch(createUpdateOrgInviteErrorAction(true))
      }
    }
  }, [isAuthenticated, acceptOrgInvite, orgInvites])

  // user traits
  useEffect((): void => {
    if (!isAuthenticated || userId === undefined) return

    const areRequiredStatsLoaded =
      traitsStatistics[StatisticsKeys.CreditCardsSourceCount].isSuccess &&
      traitsStatistics[StatisticsKeys.CreditCardsActiveSourceCount].isSuccess &&
      traitsStatistics[StatisticsKeys.CreditCardsDelegateOwnerMeCount]
        .isSuccess &&
      traitsStatistics[StatisticsKeys.CreditCardsDelegateUserMeCount]
        .isSuccess &&
      traitsStatistics[
        StatisticsKeys.VirtualCardsCardholderOrViewerMeActiveAndInactiveCount
      ].isSuccess &&
      traitsStatistics[StatisticsKeys.VirtualCardsRecipientMeCount].isSuccess

    // Identify the user with just these readily available props as soon as possible to properly track
    // page views and other events that happen before the user object and stats are loaded
    let userTraits: AnalyticsUserTraits = {
      brand_name: brandName,
      user_id: userId,
      email: email,
      is_internal: /^.*@paywithextend\.com$/.test(email),
      ...(brandName === SupportedBrands.Extend
        ? { subscription_plan_type: orgSaasTier?.tier }
        : { subscription_plan_type: 'N/A' }),
    }

    if (
      user !== undefined &&
      currentOrganization !== undefined &&
      areRequiredStatsLoaded
    ) {
      // Wait for all required data to be loaded before identifying the user with remaining traits
      // Otherwise, we'll be sending multiple identify calls with incomplete snapshots of the user

      const { partnerBanks, allIssuerBanks, hasActiveAstradaCard } =
        bankProperties

      userTraits = {
        ...userTraits,
        created_at: moment(user.createdAt).toISOString(),
        locale_code: user.locale,
        full_name: user.fullName,
        verified: user.verified,
        org_id: user.organization?.id,
        org_name: currentOrganization?.name,
        org_role: user.organization?.role,
        has_accounts:
          (traitsStatistics[StatisticsKeys.CreditCardsSourceCount].data ?? 0) >
          0,
        has_active_accounts:
          (traitsStatistics[StatisticsKeys.CreditCardsActiveSourceCount].data ??
            0) > 0,
        has_created_budgets:
          (traitsStatistics[StatisticsKeys.CreditCardsDelegateOwnerMeCount]
            .data ?? 0) > 0,
        has_budgets:
          (traitsStatistics[StatisticsKeys.CreditCardsDelegateUserMeCount]
            .data ?? 0) > 0,
        has_created_virtual_cards:
          (traitsStatistics[
            StatisticsKeys
              .VirtualCardsCardholderOrViewerMeActiveAndInactiveCount
          ].data ?? 0) > 0,
        has_virtual_cards:
          (traitsStatistics[StatisticsKeys.VirtualCardsRecipientMeCount].data ??
            0) > 0,
        has_active_astrada_card: hasActiveAstradaCard,
        issuer_banks: allIssuerBanks,
        partner_bank: partnerBanks,
      }
    }
    // Only identify the user if identify hasn't been called for this user in this session
    // or if the user traits have changed since the last identify call
    const lastTraits = analytics?.lastIdentifiedUserTraits
    if (!_.isEqual(lastTraits, userTraits)) {
      analytics?.identify(userId, userTraits)
    }
  }, [
    isAuthenticated,
    userId,
    user,
    currentOrganization,
    orgSaasTier,
    brandName,
    bankProperties,
    traitsStatistics[StatisticsKeys.CreditCardsSourceCount].isSuccess,
    traitsStatistics[StatisticsKeys.CreditCardsActiveSourceCount].isSuccess,
    traitsStatistics[StatisticsKeys.CreditCardsDelegateOwnerMeCount].isSuccess,
    traitsStatistics[StatisticsKeys.CreditCardsDelegateUserMeCount].isSuccess,
    traitsStatistics[
      StatisticsKeys.VirtualCardsCardholderOrViewerMeActiveAndInactiveCount
    ].isSuccess,
    traitsStatistics[StatisticsKeys.VirtualCardsRecipientMeCount].isSuccess,
  ])

  // analytics
  useEffect((): void => {
    // replace actual IDs in the path with ':id'
    const genericPath: string = location.pathname.replace(
      /vc_([\w]+)|cc_([\w]+)|bur_([\w]+)|u_([\w]+)|txn_([\w]+)|org_([\w]+)|org_ivt_([\w]+)|org_erc_([\w]+)|org_er_([\w]+)|ec_([\w]+)|dept_([\w]+)|conn_([\w]+)/g,
      ':id',
    )
    const pageName: AnalyticsPageName = analyticsPageNames[genericPath]
    if (pageName !== undefined) {
      analytics?.page(pageName)
    } else {
      // Ideally, we shouldn't be flooding the analytics with "unknown" pages
      // Once all pages that Product wants to track are mapped, this should be removed
      analytics?.pageLegacy(genericPath)
    }
  }, [location])

  // close toast on successful auth
  useEffect((): void => {
    if (isAuthenticated) {
      dispatch(createSetIsToastOpen(false))
    }
  }, [isAuthenticated])

  useEffect(() => {
    if (!resetPasswordEmailSent) return

    const resetPasswordTimeout = window.setTimeout(() => {
      dispatch(createResetPasswordResetStateAction())
    }, 60 * 1000)

    return () => window.clearTimeout(resetPasswordTimeout)
  }, [dispatch, resetPasswordEmailSent])

  useEffect(() => {
    if (!mfaCodeEmailSent) return

    const resetMfaCodeTimeout = window.setTimeout(
      () => {
        dispatch(createResetMfaCodeStateAction())
      },
      10 * 60 * 1000, // mfa code is valid for 10 minutes
    )

    return () => window.clearTimeout(resetMfaCodeTimeout)
  }, [dispatch, mfaCodeEmailSent])

  // datadog tracking
  useEffect(() => {
    datadogRum.startSessionReplayRecording()

    if (user) {
      datadogRum.setUser({
        id: user.id,
        orgId: user.organizationId,
      })
    } else {
      datadogRum.clearUser()
    }
  }, [user])

  return (
    <>
      <AsyncJobPollers />
      <Switch location={calculatedBackgroundLocation}>
        {PublicFlatArchitectureRoutes.map(({ path, exact, Component }) => (
          <Route exact={exact ?? true} path={path} key={String(path)}>
            <Component />
          </Route>
        ))}
        <Route {...authenticationAgnosticAppRoute.routeProps}>
          <AuthenticationAgnostic />
        </Route>
        {isAuthenticated && (
          <PusherContext.Provider value={pusher}>
            <OrgUpdateAwaiterContext.Provider value={awaitOrgUpdateOrTimeout}>
              <AstradaContextProvider>
                {/* TODO: remove hardcoded appId */}
                <IntercomProvider
                  appId="cq3taadk"
                  apiBase="https://api-iam.intercom.io"
                >
                  {/*
                  Chromeless authed pages (renderInAuthedShell: false) render
                  outside the Authed chrome; every other authed page falls
                  through to <Authed/>, which resolves it in one ordered page
                  <Switch>. This is a static partition of the page set, not a
                  gate — there is no exact-pathname match deciding a render lane.
                */}
                  <Switch location={calculatedBackgroundLocation}>
                    {AuthenticatedFlatArchitectureRoutes.filter(
                      ({ renderInAuthedShell }) =>
                        renderInAuthedShell === false,
                    ).map(({ path, exact, Component }) => (
                      <Route
                        exact={exact ?? true}
                        path={path}
                        key={String(path)}
                      >
                        <Component />
                        {isSessionExpiringPopupOpen && <SessionExpiringPopup />}
                        <Toast />
                      </Route>
                    ))}
                    <Route path={'/'}>
                      <Authed />
                    </Route>
                  </Switch>
                </IntercomProvider>
              </AstradaContextProvider>
            </OrgUpdateAwaiterContext.Provider>
          </PusherContext.Provider>
        )}
        {!isAuthenticated && <Unauthed />}
      </Switch>
    </>
  )
}

export default App
